import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { SiteForm, type SiteFormValues } from '@/components/sites/SiteForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { createSiteRequest } from '@/services/sites';
import type { CreateSiteBody, SiteDetail } from '@/types/sites';

export function SiteCreatePage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createMutation = useMutation<SiteDetail, ApiError, { body: CreateSiteBody }>({
    mutationFn: ({ body }) => createSiteRequest(body),
    onSuccess: (created) => {
      toast.success(`Site '${created.name}' created`);
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      void navigate('/sites');
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not create site', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('sites.create')) {
    return <Navigate to="/sites" replace />;
  }

  function handleSubmit(values: SiteFormValues): void {
    setErrorMessage(null);
    const body: CreateSiteBody = {
      name: values.name,
      code: values.code,
      location: values.location,
      address: values.address ?? null,
      description: values.description ?? null,
      siteHeadUserId: values.siteHeadUserId ?? null,
      isActive: values.isActive,
    };
    createMutation.mutate({ body });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create Site</h1>
        <p className="text-muted-foreground">Add a new industrial site to the platform.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>New site</CardTitle>
          <CardDescription>
            Site details, location, and an optional Site Head assignment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SiteForm
            mode="create"
            onSubmit={handleSubmit}
            submitting={createMutation.isPending}
            errorMessage={errorMessage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
